import { Icon, Tooltip, Text } from '@nimbus-ds/components';
import { InfoCircleIcon } from '@nimbus-ds/icons';
import { useTranslation } from 'react-i18next';
import { TransactionChargeDto } from '@tiendanube/common';
import { Domain } from '@tiendanube/common/src/enums';
import { DataList } from '@tiendanube/components';
import { CurrencyPrice, Stack } from 'commons/components';

interface Attributes {
  transactionCharge: TransactionChargeDto;
}

function OrderMerchantChargeAmountRow({
  transactionCharge,
}: Readonly<Attributes>): JSX.Element {
  const translationPrefix = 'orderSummaryCard.tableAmounts';
  const { t } = useTranslation(Domain.ORDERS, {
    keyPrefix: translationPrefix,
  });

  const capitalizeFirstLetter = (string: string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const tooltipContent = (() => {
    if (transactionCharge.type === 'tax') {
      return transactionCharge.description;
    }

    if (
      transactionCharge.embedded_charges &&
      transactionCharge.embedded_charges.length > 0
    ) {
      return getEmbeddedChargesTooltip(transactionCharge.embedded_charges);
    }

    return getMerchantChargeTooltip(transactionCharge.type);
  })();

  function getEmbeddedChargesTooltip(embeddedCharges: string[]): string {
    const tooltip = embeddedCharges
      .map((charge) =>
        t(`embeddedCharges.${charge}.tooltip`, { defaultValue: charge }),
      )
      .join(' + ');
    return capitalizeFirstLetter(tooltip);
  }

  function getMerchantChargeTooltip(type: string): string {
    return capitalizeFirstLetter(t(`merchantChargeTypes.${type}.tooltip`));
  }

  const amountAppearance =
    Number(transactionCharge.amount.value) === 0 ? 'default' : 'danger';

  return (
    <DataList.Row id="merchant_charge_details">
      <DataList.Cell width={70}>
        <Stack gap="1">
          <Text color="neutral-textHigh">
            {t(`merchantChargeTypes.${transactionCharge.type}.description`)}
          </Text>
          {!!tooltipContent && (
            <Tooltip content={tooltipContent} position="bottom" arrow>
              <Icon source={<InfoCircleIcon size="small" />} />
            </Tooltip>
          )}
        </Stack>
      </DataList.Cell>
      <DataList.Cell width={30}>
        <CurrencyPrice
          appearance={amountAppearance}
          price={-transactionCharge.amount.value}
          currency={transactionCharge.amount.currency}
          block
          textAlign="right"
        />
      </DataList.Cell>
    </DataList.Row>
  );
}
export default OrderMerchantChargeAmountRow;
